import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppDispatch, RootState } from '../store'

import {
  getBaseInboxes,
  putBaseInbox,
  postBaseInbox,
  deleteBaseInbox
} from '../lib/api'
import { AddErrorAlert } from './alert'

type BaseInboxState = {
  loaded: boolean
  inboxes: APIBaseInbox[]
  error: any
}

const initialState: BaseInboxState = {
  loaded: false,
  inboxes: [],
  error: null
}

const baseInboxSlice = createSlice({
  name: 'baseInbox',
  initialState,
  reducers: {
    finishLoading: (
      state: BaseInboxState,
      action: PayloadAction<APIBaseInbox[]>
    ) => {
      state.loaded = true
      state.inboxes = action.payload
    },

    clearError: (state: BaseInboxState) => {
      state.error = null
    },

    setError: (state: BaseInboxState, action: PayloadAction<any>) => {
      state.error = action.payload
      state.loaded = true
    },

    updateBox: (state: BaseInboxState, action: PayloadAction<APIBaseInbox>) => {
      const id = action.payload.id
      const idx = state.inboxes.findIndex(box => box.id === id)
      if (idx >= 0) {
        state.inboxes[idx] = action.payload
      }
    },

    createBox: (state: BaseInboxState, action: PayloadAction<APIBaseInbox>) => {
      state.inboxes.push(action.payload)
    },

    deleteBox: (state: BaseInboxState, action: PayloadAction<string>) => {
      const id = action.payload
      state.inboxes = state.inboxes.filter(box => box.id !== id)
    }
  }
})

export const { setError, clearError } = baseInboxSlice.actions
const { finishLoading, updateBox, createBox, deleteBox } =
  baseInboxSlice.actions

export const loadBaseInboxes = () => {
  return async (dispatch: AppDispatch) => {
    const inboxes = []

    try {
      dispatch(clearError())
      const data = await getBaseInboxes()
      inboxes.push(...data)
    } catch (err) {
      dispatch(AddErrorAlert(err))
    }

    dispatch(finishLoading(inboxes))
  }
}

export const updateInbox = (
  id: string,
  newData: BaseInboxUpdate,
  cb: (success: boolean) => void
) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(clearError())
      const data = await putBaseInbox(id, newData)
      dispatch(updateBox(data))
      cb(true)
    } catch (err) {
      dispatch(AddErrorAlert(err))
      cb(false)
    }
  }
}

export const createInbox = (
  newData: BaseInboxUpdate,
  cb: (success: boolean) => void
) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(clearError())
      const data = await postBaseInbox(newData)
      dispatch(createBox(data))
      cb(true)
    } catch (err) {
      dispatch(AddErrorAlert(err))
      cb(false)
    }
  }
}

export const deleteInbox = (id: string, cb: (success: boolean) => void) => {
  return async (dispatch: AppDispatch) => {
    try {
      dispatch(clearError())
      await deleteBaseInbox(id)
      dispatch(deleteBox(id))
      cb(true)
    } catch (err) {
      dispatch(AddErrorAlert(err))
      cb(false)
    }
  }
}

export const selectBaseInbox = (state: RootState) => state.baseInbox

export default baseInboxSlice.reducer
